import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import background from "../assets/background.webp";
import { useTranslation } from "react-i18next";

// Ленивая загрузка видео (импортируем только при необходимости)
const videoModules = {
  1: () => import('../assets/videos/1.mov'),
  2: () => import('../assets/videos/2.mov'),
  3: () => import('../assets/videos/3.mov'),
  4: () => import('../assets/videos/4.mp4'),
  5: () => import('../assets/videos/5.mp4'),
  6: () => import('../assets/videos/6.mp4'),
  7: () => import('../assets/videos/7.mp4'),
  8: () => import('../assets/videos/8.mp4'),
  9: () => import('../assets/videos/9.mp4'),
  10: () => import('../assets/videos/10.mp4'),
  11: () => import('../assets/videos/11.mp4'),
  12: () => import('../assets/videos/12.mp4'),
  13: () => import('../assets/videos/13.mp4'),
  14: () => import('../assets/videos/14.mp4'),
  15: () => import('../assets/videos/15.mp4'),
  16: () => import('../assets/videos/16.mp4'),
  17: () => import('../assets/videos/17.mp4'),
  18: () => import('../assets/videos/18.mp4'),
  19: () => import('../assets/videos/19.mp4'),
  20: () => import('../assets/videos/20.mp4'),
  21: () => import('../assets/videos/21.mp4'),
  22: () => import('../assets/videos/22.mp4'),
  23: () => import('../assets/videos/23.mp4'),
  24: () => import('../assets/videos/24.mp4'),
  25: () => import('../assets/videos/25.mp4'),
  26: () => import('../assets/videos/26.mp4'),
  27: () => import('../assets/videos/27.mp4'),
  28: () => import('../assets/videos/28.mp4'),
  29: () => import('../assets/videos/29.mp4'),
  30: () => import('../assets/videos/30.mp4'),
  31: () => import('../assets/videos/31.mp4'),
  32: () => import('../assets/videos/32.mp4'),
  33: () => import('../assets/videos/33.mp4'),
  34: () => import('../assets/videos/34.mov'),
  35: () => import('../assets/videos/35.mov'),
  36: () => import('../assets/videos/36.mov'),
  37: () => import('../assets/videos/37.mov'),
  38: () => import('../assets/videos/38.mov'),
  39: () => import('../assets/videos/39.mov'),
  40: () => import('../assets/videos/40.mov'),
  41: () => import('../assets/videos/41.MOV'),
  42: () => import('../assets/videos/42.mov'),
  43: () => import('../assets/videos/43.mov'),
  44: () => import('../assets/videos/44.mov'),
};

// Генерируем статические превью
const generateThumbnail = (index) => {
  return `/thumbnails/${index + 1}.png`;
};

// Компонент видеоплеера с ленивой загрузкой
const VideoPlayer = lazy(() => import('./videoplayer'));

// Оптимизированный компонент видео карточки
const VideoCard = ({ index, onClick, isVisible }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState(generateThumbnail(index));
  const [hasError, setHasError] = useState(false);

  // Загружаем видео только при наведении или если карточка видима
  useEffect(() => {
    const loadVideo = async () => {
      if ((isVisible || isHovered) && !videoSrc && !isLoading && !hasError) {
        setIsLoading(true);
        try {
          const moduleLoader = videoModules[index + 1];
          if (moduleLoader) {
            const module = await moduleLoader();
            setVideoSrc(module.default);
          } else {
            console.error(`Video ${index + 1} not found`);
            setHasError(true);
          }
        } catch (error) {
          console.error(`Error loading video ${index + 1}:`, error);
          setHasError(true);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadVideo();
  }, [isVisible, isHovered, index, videoSrc, isLoading, hasError]);

  const handleClick = () => {
    if (videoSrc) {
      onClick(videoSrc);
    } else if (!isLoading && !hasError) {
      // Если видео еще не загружено, открываем плеер сразу
      onClick(null, index);
      // И загружаем видео асинхронно
      setIsLoading(true);
      videoModules[index + 1]().then(module => {
        setVideoSrc(module.default);
        setIsLoading(false);
        // Не вызываем onClick снова, так как плеер уже открыт
      }).catch(error => {
        console.error(`Error loading video ${index + 1}:`, error);
        setIsLoading(false);
        setHasError(true);
      });
    }
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="cursor-pointer group  relative overflow-hidden h-[350px] max-md:h-[300px] bg-black 
                 shadow-[10px_10px_0_#eab308] hover:shadow-[20px_20px_0_#eab308]
                 transition-all duration-300 hover:-translate-y-1 transform-gpu"
    >
      {/* Превью */}
      <img
        src={thumbnail}
        alt={`Video ${index + 1}`}
        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
        loading="lazy"
        onError={(e) => {
          // Если превью не найдено, показываем заглушку
          e.target.src = `https://via.placeholder.com/400x300/1a1a1a/eab308?text=Video+${index + 1}`;
        }}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error overlay */}
      {hasError && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
          <div className="text-red-500 text-center">
            <div className="text-4xl mb-2">⚠️</div>
            <div className="text-sm">{t("video_error_loading")}</div>
          </div>
        </div>
      )}

      {/* Play overlay */}
      <div className={`absolute inset-0 bg-black/50 flex items-center justify-center
                     transition-opacity duration-300 ${isHovered && !hasError ? 'opacity-100' : 'opacity-0'}`}>
        <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center
                      text-black text-2xl transform transition-transform duration-300
                      group-hover:scale-110">
          ▶
        </div>
      </div>

      {/* Номер видео */}
      <div className="absolute top-2 left-2 bg-black/60 text-yellow-500 px-2 py-1 rounded
                    text-sm font-mono backdrop-blur-sm">
        #{index + 1}
      </div>
    </div>
  );
};

// Компонент для отслеживания видимости карточки
const VisibleObserver = ({ children, onVisible }) => {
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible();
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [onVisible]);

  return <div ref={ref}>{children}</div>;
};

const VideosPage = () => {
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState(null);
  const [visibleCards, setVisibleCards] = useState({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleCardVisible = (index) => {
    setVisibleCards(prev => ({ ...prev, [index]: true }));
  };

  const handleVideoClick = (videoSrc, index) => {
    if (videoSrc) {
      setActiveVideo(videoSrc);
    } else {
      // Если видео не загружено, открываем плеер с loading
      setActiveVideo({ loading: true, index: index + 1 });
    }
  };

  return (
    <section
      className="w-full min-h-screen bg-slate-800 py-24 px-6 bg-center  bg-fixed"
      style={{ backgroundImage: `url(${background})` }}
    >
      {/* TITLE */}
      <div className={`text-center pt-20 max-sm:pt-6 mb-14 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <h1 className="text-white uppercase text-[32px] md:text-[64px] tracking-[0.15em] font-['Cormorant_Garamond']">
          {t("videos_page_title")}
        </h1>
        <div className="w-24 h-1 bg-yellow-500 mx-auto mt-4 mb-2" />
        <p className="text-yellow-400 tracking-[0.4em] uppercase mt-3 text-sm">
          {t("videos_page_subtitle")}
        </p>
      </div>

      {/* GRID */}
      <div className={`w-full mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {Array.from({ length: Object.keys(videoModules).length }, (_, i) => (
            <VisibleObserver key={i} onVisible={() => handleCardVisible(i)}>
              <VideoCard
                index={i}
                onClick={handleVideoClick}
                isVisible={visibleCards[i]}
              />
            </VisibleObserver>
          ))}
        </div>
      </div>

      {/* КАСТОМНЫЙ ВИДЕОПЛЕЕР С ЛЕНИВОЙ ЗАГРУЗКОЙ */}
      {activeVideo && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/95 backdrop-blur-lg z-[99998] flex items-center justify-center transition-all duration-500">
            <div className="text-white text-xl">{t("loading_player")}</div>
          </div>
        }>
          <VideoPlayer
            videoSrc={activeVideo}
            onClose={() => setActiveVideo(null)}
          />
        </Suspense>
      )}
    </section>
  );
};

export default VideosPage;